import { ScheduleEntryState } from '@griply/types';
import { getOverduePromptConfig } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './overdue-context-menu.style';

export type OverdueContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'onOutside' | 'children'
> & {
  name: string;
  overdueDate: Timestamp;
  type?: ScheduleEntryState;
  onDate: (date: Timestamp) => void;
  onClose: () => void;
};

export const OverdueContextMenu: React.FC<OverdueContextMenuProps> = ({
  name,
  overdueDate,
  onDate,
  onClose,
  type = ScheduleEntryState.Complete,
  ...rest
}) => {
  const today = useToday();
  const { t } = useTranslation();

  const onOverdue = () => {
    onDate(overdueDate);
    onClose();
  };
  const onToday = () => {
    onDate(today);
    onClose();
  };

  const { title, overdueDateLabel, daysAgoLabel, todayLabel } = useMemo(
    () =>
      getOverduePromptConfig({ name, overdueDate, today, entryType: type, t }),
    [name, overdueDate, today, type, t],
  );

  return (
    <PopupMenu {...rest} onOutside={onClose}>
      <Styled.ListWidthLimiter>
        <PopupMenuList>
          <PopupMenuTitle title={title} />
          <PopupMenuListItem>
            <PopupMenuButton
              start={<Icon icon={Calendar16} />}
              onClick={onOverdue}
              end={<Typography variant="overline">{daysAgoLabel}</Typography>}
            >
              {overdueDateLabel}
            </PopupMenuButton>
            <PopupMenuButton
              start={<Icon icon={Calendar16} />}
              onClick={onToday}
            >
              {todayLabel}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      </Styled.ListWidthLimiter>
    </PopupMenu>
  );
};
